import DatasetDTO from 'dto/DatasetDTO';
import BriefUserInfoDTO from 'dto/BriefUserInfoDTO';
import SharedRepoOwnerDTO from 'dto/SharedRepoOwnerDTO';
import SharedRepoRecepientDTO from 'dto/SharedRepoRecepientDTO';
import api, { download, source } from 'utils/axios';

export default {
  source,
  createFolder: async (values) => {
    return await api.post('/repo/createFolder', values);
  },
  createPublicRepository: async (values) => {
    return await api.post('/repo/createPublicRepository', values);
  },
  createRepository: async (values) => {
    return await api.post('/repo/createRepository', values);
  },
  updateRepository: async (values) => {
    return await api.post('/repo/updateRepository', values);
  },
  updatePublicRepository: async (values) => {
    return await api.post('/repo/updatePublicRepository', values);
  },
  changeFolder: async (values) => {
    return await api.post('/repo/changeFolder', values);
  },
  deleteFolder: async (values) => {
    return await api.post('/repo/deleteFolder', values);
  },
  getWithFolders: async (params, cancelToken) => {
    const route = generateUrl(params, '/repo/getWithFolders');
    return await api.get(route, { cancelToken });
  },
  getPublicRepositoriesWithFolders: async (params, cancelToken) => {
    const route = generateUrl(params, '/repo/getPublicRepositoriesWithFolders');
    return await api.get(route, { cancelToken });
  },
  deleteRepositories: async (values) => {
    return await api.post('/repo/deleteRepositories', values);
  },
  deletePublicRepositories: async (values) => {
    return await api.post('/repo/deletePublicRepositories', values);
  },
  duplicateRepositories: async (values) => {
    return await api.post('/repo/duplicateRepositories', values);
  },
  duplicatePublicRepositories: async (values) => {
    return await api.post('/repo/duplicatePublicRepositories', values);
  },
  getDatasets: async (params) => {
    return await api.get('/repo/getDatasets', { params });
  },
  getPublicDatasets: async (params) => {
    return await api.get('/repo/getPublicDatasets', { params });
  },
  getSharedGroups: async (params) => {
    return await api.get('/repo/getClasses', { params });
  },
  getApplicationDatasets: async (params) => {
    return await api.get('/repo/getApplicationDatasets', { params });
  },
  createDataset: async (values) => {
    return await api.post('/repo/createDataset', values);
  },
  createPublicDataset: async (values) => {
    return await api.post('/repo/createPublicDataset', values);
  },
  addApplicationDatasetsToRepo: async (values) => {
    return await api.post('/repo/addApplicationDatasetsToRepo', values);
  },
  addApplicationDatasetsToPublicRepo: async (values) => {
    return await api.post('/repo/addApplicationDatasetsToPublicRepo', values);
  },
  getReposSharedWithMe: async(): Promise<SharedRepoRecepientDTO[]> => {
    return (await api.get('/repo/getReposIReceive')).data;
  },
  getReposIShare: async(): Promise<SharedRepoOwnerDTO[]> => {
    return (await api.get('/repo/getReposIShare')).data;
  },
  shareRepo: async(repoID: number, loginIDs: string[]): Promise<void> => {
    await api.post('/repo/shareRepo', {repoID, loginIDs});
  },
  unshareRepo: async(repoID: number, loginID: string): Promise<void> => {
    await api.post('/repo/unshareRepo', {repoID, loginID});
  },
  importDatasets: async(repoID: number, datasetIDs: number[]): Promise<void> => {
    await api.post('/repo/importDataset', {repoID, datasetIDs});
  },
  getBriefUserInfo: async(loginID: string): Promise<BriefUserInfoDTO> => {
    return (await api.get('/repo/getUser', {params: {loginID}})).data;
  },
  renameDataset: async(name: string, id: number): Promise<void> => {
    await api.post('/repo/renameDataset', {id, name});
  },
  getDoc: async(repoID: number, datasetID: number): Promise<string> => {
    return await (await api.get('/repo/getDocFile', {params: {repoID, datasetID}})).data;
  },
  getPublicDoc: async(repoID: number, datasetID: number): Promise<string> => {
    return await (await api.get('/repo/getPublicDocFile', {params: {repoID, datasetID}})).data;
  },
  updateDoc: async (repoID: number, datasetID: number, html: string): Promise<void> => {
    await api.post('/repo/updateDocFile', {repositoryID: repoID, datasetID, html});
  },
  updateDataset: async (data: {
    repoID: number,
    datasetID: number,
    html?: string,
    name?: string,
    title?: string,
    description?: string
  }): Promise<void> => {
    await api.post('/repo/updateDataset', data);
  },

  updatePublicDataset: async (data: {
    repoID: number,
    datasetID: number,
    html?: string,
    name?: string,
    title?: string,
    description?: string
  }): Promise<void> => {
    await api.post('/repo/updatePublicDataset', data);
  },

  getData: async (datasetID: number): Promise<Record<string, string>[]> => {
    const resp = await api.get('/repo/getData', {params: {datasetID}});

    return resp.data;
  },
  getPublicData: async (datasetID: number): Promise<Record<string, string>[]> => {
    const resp = await api.get('/repo/getPublicData', {params: {datasetID}});

    return resp.data;
  },

  downloadData: async (dataset: DatasetDTO): Promise<void> => {
    const resp = await api.get('/repo/downloadData', {
      params: {datasetID: dataset.id},
      responseType: 'blob'
    });

    download(resp.data, `${dataset.name}.${dataset.fileExtension}`);
  },
  downloadPublicData: async (dataset: DatasetDTO): Promise<void> => {
    const resp = await api.get('/repo/downloadPublicData', {
      params: {datasetID: dataset.id},
      responseType: 'blob'
    });

    download(resp.data, `${dataset.name}.${dataset.fileExtension}`);
  }
};

function generateUrl(params, baseUrl) {
  const ret = [];
  for (const key in params) {
    if (params[key] !== '') {
      ret.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
    }
  }
  const paramString = ret.join('&');
  return paramString ? baseUrl + '?' + paramString : baseUrl;
}
